'use client';

import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import {
    Clock,
    Shield,
    Smile,
    Heart,
    Zap,
    Users
} from 'lucide-react';
import { useTranslation } from "@/hooks/useTranslation";

export default function BenefitsSection() {
    const { t, isRTL } = useTranslation("all-on-4");
    
    const benefits = [
        {
            icon: Clock,
            title: t("benefits.items.quickRecovery.title"),
            description: t("benefits.items.quickRecovery.description")
        },
        {
            icon: Shield,
            title: t("benefits.items.highSuccessRate.title"),
            description: t("benefits.items.highSuccessRate.description")
        },
        {
            icon: Smile,
            title: t("benefits.items.naturalLook.title"),
            description: t("benefits.items.naturalLook.description")
        },
        {
            icon: Heart,
            title: t("benefits.items.noBoneGraft.title"),
            description: t("benefits.items.noBoneGraft.description")
        },
        {
            icon: Zap,
            title: t("benefits.items.immediateFunction.title"),
            description: t("benefits.items.immediateFunction.description")
        },
        {
            icon: Users,
            title: t("benefits.items.expertCare.title"),
            description: t("benefits.items.expertCare.description")
        }
    ];

    return (
        <section
            id="benefits"
            className="py-16 lg:py-24 bg-white dark:bg-slate-800 min-h-[700px]"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {t("benefits.title").includes("All-On-4") ? (
                            <>
                                {t("benefits.title").split("All-On-4")[0]}
                                <span className="text-gold">All-On-4</span>
                                {t("benefits.title").split("All-On-4")[1]}
                            </>
                        ) : t("benefits.title")}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("benefits.subtitle")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {(isRTL ? [...benefits].reverse() : benefits).map((benefit, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow duration-300">
                                <CardContent className="p-6 text-center">
                                    <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                        <benefit.icon className="w-8 h-8 text-gold" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-navy dark:text-white mb-4">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300">
                                        {benefit.description}
                                    </p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
} 